import React, { useCallback, useState } from "react";
import { AttachmentContainer } from "./styled";
import BarekamFileInput from "./file-input/file-input";
import BarekamText from "../text";
import BarekamFileRow from "./file-row/file-row";
import { FileModel } from "../../shared/models/FileModel";
import { FormHelperText } from "@mui/material";
import { MessageSections } from "../message/message";
import { ConfirmationDialog } from "../dialogs/confirmation-dialog";
import { useCalendarState } from "@mui/x-date-pickers/internals";

export const BarekamAttachment = ({ titleLabel, values, value, errorMessage, messageSection, isReadonly, isSingle, onValueChange, onValuesChange }: BarekamAttachmentProps) => {
    const [openConfirmationPopup, setOpenConfirmationPopup] = useState<boolean>(false);
    const [attachmentToDelete, setAttachmentToDelete] = useState<(File | FileModel)>();

    async function deleteAttachment(file: File | FileModel) {
        setAttachmentToDelete(file);
        setOpenConfirmationPopup(true);
    }

    const handleConfirmationDialogClose = useCallback((confirmed: boolean) => {
        setOpenConfirmationPopup(false);
        if (confirmed) {
            if (isSingle) {
                onValueChange(undefined);
            } else {
                const needToDelete = values?.filter(uploadedFile => uploadedFile !== attachmentToDelete) || [];
                onValuesChange(needToDelete);
            }
        }
    }, [onValueChange, onValuesChange, isSingle, attachmentToDelete]);

    function getUniqueFileId(file: File | FileModel, index: number) {
        if (file instanceof File) {
            return `${file.name}-${index}`
        } else {
            return file.fileId;
        }
    }

    return (
        <AttachmentContainer>
            <BarekamText variant={"m"}>
                {titleLabel}
            </BarekamText>

            {
                isReadonly || (isSingle && value)
                    ? ''
                    : <BarekamFileInput onChange={onValueChange} disabled={isReadonly} messageSection={messageSection} />
            }

            {isSingle
                ? value && <BarekamFileRow file={value}
                    disabled={isReadonly}
                    remove={deleteAttachment} />
                : <>
                    {values?.map((file, index) =>
                        <BarekamFileRow file={file}
                            disabled={isReadonly}
                            remove={deleteAttachment}
                            key={getUniqueFileId(file, index)} />
                    )}
                </>
            }

            {!!errorMessage && (
                <FormHelperText error id="input-error">
                    {errorMessage}
                </FormHelperText>
            )}

            {openConfirmationPopup && (
                <ConfirmationDialog open={openConfirmationPopup}
                    handleClose={handleConfirmationDialogClose}
                    titleKey={'delete_attachment'}
                    descriptionKey={'delete_attachment_description'} />
            )}
        </AttachmentContainer>
    )
}

export enum AttachmentType {
    INVOICE, IMAGE, RECIPIENT_IMAGE
}

interface BarekamAttachmentProps {
    titleLabel: JSX.Element,
    value?: File | FileModel | undefined;
    values?: (File | FileModel)[];
    isSingle: boolean;
    errorMessage?: string;
    messageSection?: MessageSections;

    isReadonly?: boolean;
    onValueChange?: any;
    onValuesChange?: any;
}
